import { useState, lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { Loader2, FolderOpen, ChevronDown } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import Header from "@/components/Header";
import SEOHead from "@/components/SEOHead";
import JsonLd from "@/components/JsonLd";
const BreakingTicker = lazy(() => import("@/components/BreakingTicker"));
import HeroSection from "@/components/HeroSection";
import CategoryTabs from "@/components/CategoryTabs";
import ArticleCard from "@/components/ArticleCard";
import BackToTop from "@/components/BackToTop";
import Footer from "@/components/Footer";
import SkeletonCard from "@/components/SkeletonCard";
import { Button } from "@/components/ui/button";
import { usePaginatedArticles } from "@/hooks/useArticles";
import { ArticleCategory } from "@/data/mockData";

// Lazy-load sidebar (below fold / desktop only)
const SidebarWidgets = lazy(() => import("@/components/SidebarWidgets"));

const Index = () => {
  const [activeCategory, setActiveCategory] = useState<ArticleCategory | "all">("all");
  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = usePaginatedArticles(activeCategory);

  const articles = data?.pages.flatMap((p) => p.articles) ?? [];

  return (
    <div className="min-h-screen">
      <SEOHead />
      <JsonLd type="organization" />
      <Header />
      <Suspense fallback={<div className="w-full h-[40px] bg-muted/50" />}>
        <BreakingTicker />
      </Suspense>
      <HeroSection />

      <main className="container py-8">
        <div id="wire" className="flex items-center justify-between mb-6">
          <div>
            <h2 className="font-display text-2xl font-bold mb-1">The Wire</h2>
            <p className="text-sm text-muted-foreground">
              Real-time intelligence across the Forex & Fintech ecosystem.
            </p>
          </div>
        </div>

        <div className="mb-6">
          <CategoryTabs active={activeCategory} onChange={setActiveCategory} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div key={activeCategory} className="space-y-5">
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => <SkeletonCard key={i} />)
              ) : (
                <>
                  {articles.map((article, i) => (
                    <ArticleCard key={article.id} article={article} index={i} />
                  ))}
                  {articles.length === 0 && (
                    <div className="text-center py-16 text-muted-foreground">
                      <p className="font-display text-lg">No articles in this category yet.</p>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Load More */}
            {hasNextPage && (
              <div className="text-center mt-8">
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                  className="font-display px-8"
                >
                  {isFetchingNextPage ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Loading…
                    </>
                  ) : (
                    "Load More Articles"
                  )}
                </Button>
              </div>
            )}

            {/* Mobile Explore Categories */}
            <div className="lg:hidden mt-8">
              <Collapsible>
                <CollapsibleTrigger className="w-full flex items-center justify-between rounded-lg glass p-4 group">
                  <div className="flex items-center gap-2">
                    <FolderOpen className="h-4 w-4 text-primary" />
                    <span className="font-display text-sm font-semibold">Explore Categories</span>
                  </div>
                  <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="flex flex-wrap gap-1.5 p-4 pt-3">
                    {[
                      { label: "All News", href: "/news", color: "bg-muted text-foreground" },
                      { label: "Brokers", href: "/category/brokers", color: "bg-secondary/20 text-secondary" },
                      { label: "Prop Firms", href: "/category/prop-firms", color: "bg-[hsl(var(--cat-prop-firm))]/20 text-[hsl(var(--cat-prop-firm))]" },
                      { label: "Regulation", href: "/category/regulation", color: "bg-destructive/20 text-destructive" },
                      { label: "Exposés", href: "/category/exposes", color: "bg-[hsl(var(--wire-red))]/20 text-[hsl(var(--wire-red))]" },
                      { label: "Reviews", href: "/category/reviews", color: "bg-[hsl(var(--wire-yellow))]/20 text-[hsl(var(--wire-yellow))]" },
                      { label: "Fintech", href: "/category/fintech", color: "bg-primary/20 text-primary" },
                      { label: "Analysis", href: "/category/analysis", color: "bg-[hsl(var(--wire-purple))]/20 text-[hsl(var(--wire-purple))]" },
                    ].map((cat) => (
                      <Link
                        key={cat.href}
                        to={cat.href}
                        className={`${cat.color} px-3 py-1 rounded-full text-xs font-display font-semibold transition-colors duration-200 hover:shadow-md`}
                      >
                        {cat.label}
                      </Link>
                    ))}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </div>
          </div>
          <div className="hidden lg:block">
            <Suspense fallback={<div className="space-y-6">{Array.from({length: 3}).map((_, i) => <div key={i} className="rounded-lg bg-card animate-shimmer h-48" />)}</div>}>
              <SidebarWidgets />
            </Suspense>
          </div>
        </div>
      </main>

      <Footer />
      <BackToTop />
    </div>
  );
};

export default Index;
